import { Button, Tooltip } from '@eye-able/ui';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import {
  type AuditEntryPinVariables,
  usePinAuditEntry,
  useUnpinAuditEntry,
} from '@http/queries/useAuditEntryPin';
import { api } from '@http/constants';
import { useIsMutating } from '@tanstack/react-query';
import { Pin } from 'lucide-react';
import { cn } from '@helpers/cn';
import { shortenDomain } from '@helpers/shortenDomain';

interface DomainFavoriteToggleProps {
  entry: {
    id: number;
    domain: string;
    domainAlias?: string | null;
    isPinned?: boolean;
  };
  disabled?: boolean;
  /**
   * Pointer-only copy for places assistive technology cannot reach, such as
   * listbox options. Hidden from the accessibility tree and the tab order, so
   * it needs an accessible toggle elsewhere on the page.
   */
  pointerOnly?: boolean;
}

/**
 * Toggles whether a domain is a favourite. Keep the accessible variant out of
 * listbox options: role="option" flattens its children, so it would be
 * unreachable there.
 */
export function DomainFavoriteToggle({
  entry,
  disabled = false,
  pointerOnly = false,
}: DomainFavoriteToggleProps) {
  const { t } = useInlineTranslation();
  const pinAuditEntry = usePinAuditEntry();
  const unpinAuditEntry = useUnpinAuditEntry();
  const isPinned = Boolean(entry.isPinned);
  // Shared per domain: the trigger and a list pin can toggle the same one.
  const isPending =
    useIsMutating({
      mutationKey: [api.auditEntryPins],
      predicate: (mutation) =>
        (mutation.state.variables as AuditEntryPinVariables | undefined)
          ?.auditEntryId === entry.id,
    }) > 0;
  const domainName = entry.domainAlias || shortenDomain(entry.domain);

  const handleClick = () => {
    const mutation = isPinned ? unpinAuditEntry : pinAuditEntry;
    mutation.mutate({ auditEntryId: entry.id });
  };

  return (
    <Tooltip content={t('favorite')} delayDuration={1000}>
      <Button
        className='eye:pointer-events-auto'
        type='button'
        variant='ghost'
        size='icon-xs'
        // A fixed name plus aria-pressed: a label that flips between add and
        // remove would announce the state twice.
        aria-label={t('favoriteDomain', { domain: domainName })}
        aria-pressed={isPinned}
        aria-hidden={pointerOnly || undefined}
        tabIndex={pointerOnly ? -1 : undefined}
        disabled={disabled || isPending}
        // Toggled while focused, so keyboard users must not lose their place.
        focusableWhenDisabled
        onClick={(event) => {
          stopPropagationAndDefault(event);
          handleClick();
        }}
        // Keeps a pointer press from opening the popup or selecting an option.
        onPointerDown={stopPropagationAndDefault}
        onMouseDown={stopPropagationAndDefault}
      >
        <Pin
          aria-hidden
          className={cn({ 'eye:fill-current eye:text-blue-100': isPinned })}
        />
      </Button>
    </Tooltip>
  );
}

function stopPropagationAndDefault(event: {
  preventDefault: () => void;
  stopPropagation: () => void;
}) {
  event.preventDefault();
  event.stopPropagation();
}
